import { useTranslation } from 'react-i18next'
import { usePageContext } from 'pageContext'
import { isNilOrEmpty, useAuthState } from 'packages/core'
import Search from './Search'
import DateTimeFilter from './DateTimeFilter'
import PersonFilter from './PersonFilter'
import TargetSystemFilter from './TargetSystemFilter'
import { Card } from 'packages/eid-ui'
import AdvancedFilter from './EidFilter/AdvancedFilter'
import { Icon } from 'packages/eid-icons'
import { Box } from '@mui/material'
import ShowAzureAppsFilter from './ShowOnlyAzureAppsFilter'
import GlobalRightsFilter from './GlobalRightsFilter'
import ApplicationsFilter from './ApplicationsFilter'
import UNCPathFilter from './UNCPathFilter'
import LocationFilter from './LocationFilter'
import BusinessFunctionFilter from './BusinessFunctionsFilter'
import { useGroupTypes, useHighLevelClassifications } from 'hooks'
import IncludeBasicClaimSetFilter from './IncludeBasicClaimSet'
import MailboxTypeFilter from './Mailboxes/MailboxTypeFilter'

const TextSearchFilter = ({ outlined = false, placeholder = '' }) => {
    const [state, dispatch] = usePageContext()

    return (
        <Search
            outlined={outlined}
            handleSearch={(searchKey) =>
                dispatch({
                    type: 'SET_PROP',
                    payload: {
                        key: 'searchTerm',
                        value: searchKey,
                    },
                })
            }
            handleClear={(searchKey) =>
                dispatch({
                    type: 'SET_PROP',
                    payload: {
                        key: 'searchTerm',
                        value: searchKey,
                    },
                })
            }
            value={state.searchTerm}
            placeholder={placeholder}
        />
    )
}

const StartDateFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()

    return (
        <DateTimeFilter
            disableFuture
            showLabelWithin
            placeholder={t('BetweenDate')}
            value={
                isNilOrEmpty(state.startDate)
                    ? null
                    : state.startDate.clone().local()
            }
            onChange={(date) => {
                const dateToSet = date.clone()
                dateToSet.startOf('day')
                dateToSet.utc()

                dispatch({
                    type: 'SET_PROP',
                    payload: {
                        key: 'startDate',
                        value: dateToSet,
                    },
                })
            }}
            handleClear={() =>
                dispatch({
                    type: 'SET_PROP',
                    payload: {
                        key: 'startDate',
                        value: null,
                    },
                })
            }
        />
    )
}

const EndDateFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()

    return (
        <DateTimeFilter
            disableFuture
            showLabelWithin
            placeholder={t('AndDate')}
            value={
                isNilOrEmpty(state.endDate)
                    ? null
                    : state.endDate.clone().local()
            }
            onChange={(date) => {
                const dateToSet = date.clone().endOf('day')
                dateToSet.utc()

                dispatch({
                    type: 'SET_PROP',
                    payload: {
                        key: 'endDate',
                        value: dateToSet,
                    },
                })
            }}
            handleClear={() =>
                dispatch({
                    type: 'SET_PROP',
                    payload: {
                        key: 'endDate',
                        value: null,
                    },
                })
            }
        />
    )
}

const dateFilterStyles = {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '15px',
    boxShadow: '0 4px 6px -2px rgba(0, 0, 0, 0.1)',
    border: 'solid 1px #ebebed',
    borderRadius: '5px',
    backgroundColor: '#ffffff',
    color: '#7D7C7C',
}

const DateFilter = ({ label }) => {
    return (
        <Box style={dateFilterStyles}>
            <Box minWidth="120px">{label}</Box>
            <StartDateFilter />
            <EndDateFilter />
        </Box>
    )
}

const OwnedByFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()
    const [{ currentUserId, friendlyName }] = useAuthState()

    return (
        <Card
            cardTitle={t('OwnedBy')}
            collapsible
            expanded={state.ownedBy !== null}
        >
            <PersonFilter
                value={state.ownedBy}
                onClear={() =>
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'ownedBy',
                            value: {
                                id: currentUserId,
                                friendlyName: friendlyName,
                            },
                        },
                    })
                }
                onChange={(payload) => {
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'ownedBy',
                            value: payload,
                        },
                    })
                }}
            />
        </Card>
    )
}

const ManagementRolesLocationFilter = () => {
    return (
        <LocationFilter
            url="/api/Lookups/getBusinessLocationTree?locationFor=3"
            queryKey={['MANAGEMENT ROLES', 'LOCATIONS']}
            queryParam={{
                name: 'locationId',
                valueProp: 'id',
            }}
        />
    )
}

const GroupsLocationFilter = () => {
    return (
        <LocationFilter
            url="/api/Lookups/getBusinessLocationTree?locationFor=2"
            queryKey={['GROUPS', 'LOCATIONS']}
            queryParam={{
                name: 'locationId',
                valueProp: 'id',
            }}
        />
    )
}

const PeopleLocationFilter = () => {
    return (
        <LocationFilter
            url="/api/Lookups/getBusinessLocationTree?locationFor=1"
            queryKey={['PEOPLES', 'LOCATIONS']}
            queryParam={{
                name: 'locationId',
                valueProp: 'id',
            }}
        />
    )
}

const GroupsAdvancedSearchFilters = () => {
    const { t } = useTranslation()
    const [state] = usePageContext()
    const { data: groupTypes } = useGroupTypes()
    const { data: highLevelClassifications } = useHighLevelClassifications()
    const groupsAdvancedSearchforms = {
        title: 'Common_Forms',
        colorCode: {
            primary: '#2920ad',
            secondary: '#ebeaf7',
        },
        filters: [
            {
                name: 'friendlyName',
                type: 'text',
                label: 'Common_Name',
                placeholder: 'Common_Name',
                contextProp: 'advancedSearch.forms.friendlyName',
            },
            {
                name: 'name',
                type: 'text',
                label: 'TechnicalName',
                placeholder: 'TechnicalName',
                contextProp: 'advancedSearch.forms.name',
            },
            {
                name: 'highLevelClassification',
                type: 'autocomplete',
                label: 'ByHighLevelClassification',
                placeholder: 'HighLevelClassification',
                contextProp: 'highLevelClassification',
                defaultOptions: highLevelClassifications
                    ? highLevelClassifications
                    : [],
                optionConfig: { labelProp: 'friendlyName', type: 'label' },
                supportsServerSideSearch: false,
            },

            {
                name: 'groupType',
                type: 'autocomplete',
                label: 'ByGroupType',
                placeholder: 'GroupType',
                contextProp: 'groupType',
                defaultOptions: groupTypes ? groupTypes : [],
                optionConfig: { labelProp: 'friendlyName', type: 'label' },
                supportsServerSideSearch: false,
            },
            {
                name: 'description',
                type: 'text',
                label: 'Common_Description',
                placeholder: 'Common_Description',
                contextProp: 'advancedSearch.forms.description',
            },
        ],
        tags: {
            title: 'Common_Tags',
            colorCode: {
                primary: '#f81983',
                secondary: '#fee8f2',
            },
            contextProp: 'advancedSearchTags',
        },
    }

    return (
        <Card
            cardTitle={t('Common_AdvancedSearch')}
            collapsible
            icon={<Icon name="AdvancedSearch" color="#959598" />}
            expanded={
                state['advancedSearch.forms.friendlyName'] ||
                state['advancedSearch.forms.description'] ||
                state['advancedSearch.forms.name'] ||
                state['groupType'] ||
                state['highLevelClassification'] ||
                state['advancedSearchTags']
            }
        >
            <Box padding="20px 20px 0px 20px ">
                <AdvancedFilter
                    forms={groupsAdvancedSearchforms}
                    tags={groupsAdvancedSearchforms.tags}
                />
            </Box>
        </Card>
    )
}

const PeopleAdvancedSearchFilters = () => {
    const { t } = useTranslation()
    const [state] = usePageContext()
    const enabledOptions = [
        {
            friendlyName: 'No',
            value: 'false',
        },
        {
            friendlyName: 'Yes',
            value: 'true',
        },
    ]
    const peopleAdvancedSearchforms = {
        title: 'Common_Forms',
        colorCode: {
            primary: '#2920ad',
            secondary: '#ebeaf7',
        },
        filters: [
            {
                name: 'enabled',
                type: 'autocomplete',
                label: 'Enabled',
                placeholder: 'Enabled',
                contextProp: 'advancedSearch.forms.enabled',
                defaultOptions: enabledOptions ? enabledOptions : [],
                optionConfig: { labelProp: 'friendlyName', type: 'label' },
                supportsServerSideSearch: false,
            },
            {
                name: 'friendlyName',
                type: 'text',
                label: 'Name',
                placeholder: 'Name',
                contextProp: 'advancedSearch.forms.friendlyName',
            },
            {
                name: 'login',
                type: 'text',
                label: 'Login',
                placeholder: 'Login',
                contextProp: 'advancedSearch.forms.login',
            },
            {
                name: 'email',
                type: 'text',
                label: 'Email',
                placeholder: 'Email',
                contextProp: 'advancedSearch.forms.email',
            },
        ],
    }

    return (
        <Card
            cardTitle={t('Common_AdvancedSearch')}
            collapsible
            icon={<Icon name="AdvancedSearch" color="#959598" />}
            expanded={
                state['advancedSearch.forms.friendlyName'] ||
                state['advancedSearch.forms.login'] ||
                state['advancedSearch.forms.email'] ||
                state['advancedSearch.forms.enabled']
            }
        >
            <Box padding="20px 20px 0px 20px ">
                <AdvancedFilter forms={peopleAdvancedSearchforms} />
            </Box>
        </Card>
    )
}

const AdvancedSearchFilters = ({ config }) => {
    const { t } = useTranslation()
    const [state] = usePageContext()

    return (
        <Card
            cardTitle={t('Common_AdvancedSearch')}
            collapsible
            icon={<Icon name="AdvancedSearch" color="#959598" />}
            expanded={
                config.filters.some((x) => state[x.contextProp]) ||
                state[config.tags.contextProp]
            }
        >
            <Box padding="20px 20px 0px 20px ">
                <AdvancedFilter forms={config} tags={config.tags} />
            </Box>
        </Card>
    )
}

const Filters = () => <></>

Filters.TextSearch = TextSearchFilter
Filters.StartDate = StartDateFilter
Filters.EndDate = EndDateFilter
Filters.DateFilter = DateFilter
Filters.TargetSystemFilter = TargetSystemFilter
Filters.OwnedByFilter = OwnedByFilter
Filters.ShowAzureAppsFilter = ShowAzureAppsFilter
Filters.GlobalRightsFilter = GlobalRightsFilter
Filters.ApplicationsFilter = ApplicationsFilter
Filters.ManagementRolesLocationFilter = ManagementRolesLocationFilter
Filters.GroupsLocationFilter = GroupsLocationFilter
Filters.PeopleLocationFilter = PeopleLocationFilter
Filters.BusinessFunctionFilter = BusinessFunctionFilter
Filters.IncludeBasicClaimSet = IncludeBasicClaimSetFilter
Filters.MailboxTypeFilter = MailboxTypeFilter
Filters.GroupsAdvancedSearchFilters = GroupsAdvancedSearchFilters
Filters.PeopleAdvancedSearchFilters = PeopleAdvancedSearchFilters
Filters.AdvancedSearchFilters = AdvancedSearchFilters

const filtersMap = {
    OwnedByFilter,
    showOnlyAzureApplications: ShowAzureAppsFilter,
    targetSystemFilter: TargetSystemFilter,
    globalRightsFilter: GlobalRightsFilter,
    applicationsFilter: ApplicationsFilter,
    managementRolesLocationFilter: ManagementRolesLocationFilter,
    groupsLocationFilter: GroupsLocationFilter,
    peopleLocationFilter: PeopleLocationFilter,
    businessFunctionFilter: BusinessFunctionFilter,
    groupsAdvancedSearch: GroupsAdvancedSearchFilters,
    peopleAdvancedSearch: PeopleAdvancedSearchFilters,
    uncPathFilter: UNCPathFilter,
    includeBasicClaimSet: IncludeBasicClaimSetFilter,
    advancedSearch: AdvancedSearchFilters,
    mailboxType: MailboxTypeFilter,
}

Filters.filtersMap = filtersMap

export default Filters
